import { widgetRegistry, CanvasWidgetDef } from './widgetRegistry';

/**
 * A resolved canvas — a widget from the registry plus the list of agent def IDs
 * that contribute data to it.
 */
export interface ResolvedCanvas {
  widget: CanvasWidgetDef;
  /** Which agent def IDs contribute events to this canvas */
  sourceAgentDefIds: string[];
}

/**
 * Legacy fallback: maps old userAppDefId to a canvasType.
 * Used when agent defs don't yet have metadata.ui populated.
 */
const LEGACY_APPDEFID_TO_CANVAS: Record<string, string> = {
  molecularViewer3D: 'molecularViewer',
  sequenceAlignmentViewer: 'msaViewer',
  genomicBrowserApp: 'genomicBrowser',
  drugDiscoveryDashboard: 'drugDiscovery',
  pipelineNotebook: 'notebook',
  literatureMapApp: 'knowledgeGraph',
  maintenanceConsole: 'maintenanceConsole',
  iotTelemetryDashboard: 'telemetryDashboard',
  visionInspector: 'visionInspector',
  documentQnA: 'chat',
  generalQnA: 'chat',
  studyAssistant: 'chat',
  agentOutputViewer: 'sourceEntityViewer',
  syllabusQuiz: 'quiz',
};

/**
 * Resolves which canvas widgets to display based on the sourceAgentDefs
 * declared on the user_app_def.
 *
 * For each agent def ID in sourceAgentDefs, looks up that agent's
 * metadata.ui.canvasTypes[] and maps each entry to the widget registry.
 * Deduplicates by canvasType and returns sorted by tabPriority.
 *
 * Since canvasTypes is an array, a single agent (e.g., admetFilterAgent)
 * can contribute to multiple canvases (e.g., both genomicBrowser and drugDiscovery).
 *
 * @param sourceAgentDefs - The agent def IDs from userAppDef.sourceAgentDefs
 * @param agentDefMetadataMap - Pre-fetched map of agentDefId → metadata.ui
 * @param appDefId - Optional: the userAppDefId for legacy fallback
 */
export function resolveCanvases(
  sourceAgentDefs: string[],
  agentDefMetadataMap: Record<string, { canvasTypes?: string[] }>,
  appDefId?: string,
): ResolvedCanvas[] {
  const canvasMap = new Map<string, ResolvedCanvas>();

  for (const agentDefId of sourceAgentDefs) {
    const uiMeta = agentDefMetadataMap[agentDefId];
    const canvasTypes = uiMeta?.canvasTypes || [];

    for (const canvasType of canvasTypes) {
      const widget = widgetRegistry[canvasType];
      if (!widget) continue;

      if (canvasMap.has(canvasType)) {
        canvasMap.get(canvasType)!.sourceAgentDefIds.push(agentDefId);
      } else {
        canvasMap.set(canvasType, {
          widget,
          sourceAgentDefIds: [agentDefId],
        });
      }
    }
  }

  // If no canvases resolved from agent metadata, fall back to legacy mapping
  if (canvasMap.size === 0 && appDefId) {
    const fallbackCanvas = LEGACY_APPDEFID_TO_CANVAS[appDefId];
    if (fallbackCanvas && widgetRegistry[fallbackCanvas]) {
      return [{ widget: widgetRegistry[fallbackCanvas], sourceAgentDefIds: [] }];
    }
    // Ultimate fallback: sourceEntityViewer
    return [{ widget: widgetRegistry['sourceEntityViewer'], sourceAgentDefIds: [] }];
  }

  // Sort by tabPriority (highest first)
  return Array.from(canvasMap.values()).sort(
    (a, b) => b.widget.tabPriority - a.widget.tabPriority,
  );
}
